<template>
  <v-tooltip class="lexon-tooltip-container" v-bind="tooltipPosition" :content-class="positionClass">
    <template v-slot:activator="{ on, attrs }">
      <div class="activator" v-bind="attrs" v-on="on">
        <slot name="activator-element">
          <span :class="[icon, 'tooltip-icon']" @click="(event) => action(event)"></span>
        </slot>
      </div>
    </template>
    <p class="lefebvre-tooltip-message" v-html="message"></p>
  </v-tooltip>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import { Icons } from '@/icons/icons'

// PROPS ============================================================================================================
const props = defineProps({
  position: {
    type: String as () => 'top' | 'left' | 'bottom' | 'right',
    default: 'right'
  },
  action: {
    type: Function,
    default: () => {}
  },
  icon: {
    type: String,
    default: Icons.INFO
  },
  message: {
    type: String,
    required: true
  },
  isWidthAuto: {
    type: Boolean,
    default: false
  }
})

// COMPUTED =========================================================================================================
const positionClass = computed(() => {
  switch (props.position) {
    case 'top':
      return `lexon-tooltip-component--top ${props.isWidthAuto ? 'width-auto' : ''}`
    default:
      return `lexon-tooltip-component ${props.isWidthAuto ? 'width-auto' : ''}`
  }
})

const tooltipPosition = computed(() => {
  switch (props.position) {
    case 'top':
      return { top: true }
    case 'bottom':
      return { bottom: true }
    case 'left':
      return { left: true }
    default:
      return { right: true }
  }
})
</script>

<style lang="scss" scoped>
.activator {
  align-self: var(--activator-align-self, stretch);
  line-height: var(--activator-line-height, 14px);
  overflow: var(--activator-overflow, visible);
}

.tooltip-icon {
  font-size: var(--tooltip-icon-size, 18px);
  padding-top: 2px;
  margin-left: var(--tooltip-icon-margin-left, 10px);
  color: var(--tooltip-icon-color, $neutral-grey-800);
  cursor: pointer;
}

.lefebvre-tooltip-message {
  word-wrap: break-word;
  overflow-wrap: break-word;
  max-width: 100%;
}
</style>

<style lang="scss">
@mixin shared {
  opacity: 1 !important;
  z-index: 9999 !important;
  display: flex;
  max-width: 200px;
  padding: $spacing-xs;
  flex-direction: column;
  align-items: flex-start;
  gap: $spacing-xxs;
  border-radius: $cornerRadius-sm;
  background: $main-050;

  &.width-auto {
    max-width: 100%;
  }

  .lefebvre-tooltip-message {
    @include milano-regular-14;
    opacity: 1 !important;
    padding: 10px;
    margin-bottom: 0 !important;
    color: $neutral-grey-800;
  }
}

.v-tooltip__content.lexon-tooltip-component {
  @include shared;

  &--top {
    @include shared;
    filter: drop-shadow(0 2px 4px rgba(113, 113, 113, 0.25));

    &::after {
      content: '';
      display: block;
      position: absolute;
      bottom: -20px;
      left: 50%;
      width: 0;
      height: 0;
      border-bottom: 10px solid transparent;
      border-top: 10px solid $main-050;
      border-left: 10px solid transparent;
      border-right: 10px solid transparent;
      transform: translateX(-50%);
    }
  }

  &--right {
    @include shared;
    filter: drop-shadow(-2px 0 4px rgba(113, 113, 113, 0.25));

    &::after {
      content: '';
      display: block;
      position: absolute;
      left: -20px;
      top: 50%;
      width: 0;
      height: 0;
      border-right: 10px solid $main-050;
      border-top: 10px solid transparent;
      border-bottom: 10px solid transparent;
      border-left: 10px solid transparent;
      transform: translateY(-50%);
    }
  }
}
</style>
